import React from 'react';
import PropTypes from 'prop-types';
import * as echarts from 'echarts';
import ReactEcharts from 'echarts-for-react';
import { useTranslation } from 'react-i18next';

import useStyles from './styles';
import translateKey from '../translations';

const maleColorObject = new echarts.graphic.LinearGradient(
  0, 0, 1, 0,
  [{ offset: 0, color: '#0A41BF' }, { offset: 1, color: '#2F6BF4' }],
);

const femaleColorObject = new echarts.graphic.LinearGradient(
  0, 0, 1, 0,
  [{ offset: 0, color: '#BA3312' }, { offset: 1, color: '#FF3301' }],
);

const categoriesSorterHelper = x => parseInt(x.slice(0, 2), 10);
const categoriesSorter = (a, b) => categoriesSorterHelper(a) - categoriesSorterHelper(b);
const entitySorter = (a, b) =>
  categoriesSorterHelper(a.category) - categoriesSorterHelper(b.category);

const uniq = (v, i, a) => {
  const result = (a.indexOf(v) === i);
  return result;
};

const GenderBarEchart = ({ title, data }) => {
  const { t } = useTranslation(['translation'])

  const totals = data.reduce(
    (acc, val) => {
      acc[val.gender] += val.value;
      return acc;
    },
    { male: 0, female: 0 },
  );

  const categories = data.map(x => x.category).filter(uniq).sort(categoriesSorter);
  const series = [
    {
      name: 'female',
      type: 'bar',
      data: data.filter(x => x.gender === 'female').sort(entitySorter),
      itemStyle: {
        color: femaleColorObject,
      },
    },
    {
      name: 'male',
      type: 'bar',
      data: data.filter(x => x.gender === 'male').sort(entitySorter),
      itemStyle: {
        color: maleColorObject,
      },
    },
  ];

  const options = {
    tooltip: {
      backgroundColor: 'rgba(50,50,50,0.7)',
      trigger: 'axis',
      formatter: (params) => {
        const topLine = `${params[0].name}`;
        const bottomLines = params.map(p => {
          const perc = totals[p.seriesName] ? p.value / totals[p.seriesName] : 0;
          return `${t(translateKey(p.seriesName))}: ${t('COMMON.INTL_NUMBER', { value: p.value })} (${t('COMMON.INTL_PERCENT_WITH_ONE_DECIMAL', { value: perc })})`;
        });
        return `${topLine}<br/>${bottomLines.join('<br/>')}`;
      },

      axisPointer: {
        type: 'shadow',
      },
      confine: true,
      textStyle: {
        color: 'white',
        fontSize: 18,
        fontWeight: 600,
        textAlign: 'left',
        align: 'left',
      },
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true,
    },
    xAxis: {
      show: false,
      type: 'value',
    },
    yAxis: {
      axisLabel: {
        color: '#BDC3C7',
        fontFamily: 'Poppins, sans-serif',
        fontSize: 12,
        fontWeight: 'bold',
      },
      axisLine: {
        show: false,
        symbol: 'arrow',
      },
      axisTick: {
        show: false,
      },
      splitLine: {
        show: false,
      },
      type: 'category',
      nameTextStyle: {
        color: 'green',
      },
      data: categories,
      inverse: true,
    },
    series,
  };

  const classes = useStyles();
  return (
    <div className={classes.container}>
      <div className={classes.headerContainer}>
        <p className={classes.title}>{title}</p>
        <div className={classes.legendContainer}>
          <p className={classes.femaleText}>{t('ANALYTICS.CHARTS.FEMALE')}</p>
          <p className={classes.maleText}>{t('ANALYTICS.CHARTS.MALE')}</p>
        </div>
      </div>
      <ReactEcharts
        option={options}
        notMerge
        style={{ width: '90%', height: '440px' }}
      />
    </div>
  );
};

GenderBarEchart.propTypes = {
  title: PropTypes.string.isRequired,
  data: PropTypes.arrayOf(PropTypes.shape({
    gender: PropTypes.string,
    category: PropTypes.string,
    value: PropTypes.number,
  })).isRequired,
};

export default GenderBarEchart;
